import { downloadFile } from '../utils/download'
import { isPinpoint } from './templateLibrary'

// Characters no file system accepts in a name (Windows is the strictest).
const UNSAFE_IN_FILE_NAME = /[\\/:*?"<>|]/g
// Names are user-typed and can run to a sentence; keep the file name usable.
const MAX_NAME_LENGTH = 120

/**
 * The template's body from a preview response (GET /comms/template/preview),
 * or '' when it carries none.
 *
 * The API answers `{file_data, comm_type}` for a body stored in S3 (most
 * templates) but the body itself, as a plain string, for a template whose
 * body is inline (older SMS/WhatsApp rows, a few alert templates). The
 * preview dialog, the download, Bulk Send and Edit Template all read it
 * through here, so none can mistake one shape for the other.
 *
 * @param {string|object|undefined} response the body's `response` field
 * @returns {string}
 */
export const previewBody = (response) => {
    if (typeof response === 'string') return response
    const fileData = response?.file_data
    return typeof fileData === 'string' ? fileData : ''
}

/**
 * The file name a template downloads as: its name made safe for any file
 * system, then `.html` for an email (its body is the email's HTML, so it
 * opens in a browser) or `.txt` for every other channel (plain text with
 * `${variable}` placeholders; a voice body is SSML).
 *
 * Characters a file system refuses (`/ \ : * ? " < > |` and control
 * characters) become `_`, runs of spaces collapse, and leading or trailing
 * dots and spaces go. A name with nothing left becomes `template`.
 *
 * @param {string} name the template's name (its id when the name is unknown)
 * @param {string} commType the template's channel
 * @returns {string}
 */
export const templateFileName = (name, commType) => {
    const safe = [...String(name ?? '').replace(UNSAFE_IN_FILE_NAME, '_')]
        .map((ch) => (ch.charCodeAt(0) < 32 || ch === '\u007f' ? '_' : ch))
        .join('')
        .replace(/\s+/g, ' ')
        .slice(0, MAX_NAME_LENGTH)
        .replace(/^[.\s]+|[.\s]+$/g, '')
    return `${safe || 'template'}${commType === 'email' ? '.html' : '.txt'}`
}

/**
 * Why a template from the list cannot be downloaded, or null when it can.
 *
 * A template with no stored body has nothing to download: WhatsApp templates
 * created or migrated in Meta keep their text there, and Slack templates made
 * before services#12442 were saved without one. The wording follows the
 * preview API's own reason (`preview_unavailable_reason`). An Amazon
 * Pinpoint row has no `body` but can be downloaded: Pinpoint answers its
 * text.
 *
 * @param {{comm_type: string, body: string}} template a row of GET /comms/templates
 * @returns {string|null}
 */
export const downloadUnavailableReason = (template) => {
    if (template?.body || isPinpoint(template)) return null
    return template?.comm_type === 'whatsapp'
        ? 'Managed in Meta: comms keeps no copy of its text to download'
        : 'This template has no stored body to download'
}

/**
 * Save a template's body as a file named after it (see templateFileName).
 *
 * @param {string} body the template's body, exactly as stored
 * @param {{name: string, commType: string}} template
 * @returns {string} the file name it was saved under
 */
export const saveTemplate = (body, { name, commType }) => {
    const fileName = templateFileName(name, commType)
    downloadFile(
        body,
        fileName,
        commType === 'email'
            ? 'text/html;charset=utf-8'
            : 'text/plain;charset=utf-8'
    )
    return fileName
}
